import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  hasOwn,
  normalizeTagIdsValue,
  toPositiveInt,
} from "../client";

function normalizeAdminContentBankItemPayload(input = {}) {
  const src = input && typeof input === "object" ? input : {};
  const payload = {};

  if (hasOwn(src, "bank_id")) {
    payload.bank_id = toPositiveInt(src.bank_id);
  }

  if (hasOwn(src, "task_id")) {
    payload.task_id = toPositiveInt(src.task_id);
  }

  if (hasOwn(src, "title")) {
    payload.title = String(src.title ?? "").trim();
  }

  if (hasOwn(src, "description")) {
    payload.description = src.description == null ? "" : String(src.description);
  }

  if (hasOwn(src, "components_json")) {
    payload.components_json = Array.isArray(src.components_json)
      ? src.components_json
      : src.components_json ?? [];
  }

  if (hasOwn(src, "is_active")) {
    payload.is_active = Number(src.is_active) ? 1 : 0;
  }

  if (hasOwn(src, "order_index")) {
    payload.order_index = toPositiveInt(src.order_index);
  }

  if (hasOwn(src, "tag_ids")) {
    payload.tag_ids = normalizeTagIdsValue(src.tag_ids);
  }

  return payload;
}

function normalizeContentBankPayload(input = {}) {
  return {
    title: String(input?.title ?? "").trim(),
    description: input?.description == null ? "" : String(input.description),
    is_active: Number(input?.is_active) === 0 ? 0 : 1,
  };
}

export async function fetchAdminContentBanks() {
  return apiGet("/admin/content-bank/banks");
}

export async function fetchAdminContentBankPaths(bankId) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("content bank id invalid");
  return apiGet(`/admin/content-bank/banks/${id}/paths`);
}

export async function createAdminContentBankPath(bankId, data) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("content bank id invalid");
  return apiPost(`/admin/content-bank/banks/${id}/paths`, {
    title: String(data?.title ?? "").trim(),
  });
}

export async function updateAdminContentBankPath(pathId, data) {
  const id = toPositiveInt(pathId);
  if (!id) throw new Error("content bank path id invalid");
  return apiPut(`/admin/content-bank/paths/${id}`, {
    title: String(data?.title ?? "").trim(),
    order_index: toPositiveInt(data?.order_index),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  });
}

export async function deleteAdminContentBankPath(pathId) {
  const id = toPositiveInt(pathId);
  if (!id) throw new Error("content bank path id invalid");
  return apiDelete(`/admin/content-bank/paths/${id}`);
}

export async function fetchAdminContentBankSections(pathId) {
  const id = toPositiveInt(pathId);
  if (!id) throw new Error("content bank path id invalid");
  return apiGet(`/admin/content-bank/paths/${id}/sections`);
}

export async function createAdminContentBankSection(pathId, data) {
  const id = toPositiveInt(pathId);
  if (!id) throw new Error("content bank path id invalid");
  return apiPost(`/admin/content-bank/paths/${id}/sections`, {
    title: String(data?.title ?? "").trim(),
  });
}

export async function updateAdminContentBankSection(sectionId, data) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("content bank section id invalid");
  return apiPut(`/admin/content-bank/sections/${id}`, {
    title: String(data?.title ?? "").trim(),
    order_index: toPositiveInt(data?.order_index),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  });
}

export async function deleteAdminContentBankSection(sectionId) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("content bank section id invalid");
  return apiDelete(`/admin/content-bank/sections/${id}`);
}

export async function fetchAdminContentBankHierarchyTasks(sectionId) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("content bank section id invalid");
  return apiGet(`/admin/content-bank/sections/${id}/tasks`);
}

export async function createAdminContentBankHierarchyTask(sectionId, data) {
  const id = toPositiveInt(sectionId);
  if (!id) throw new Error("content bank section id invalid");
  return apiPost(`/admin/content-bank/sections/${id}/tasks`, {
    title: String(data?.title ?? "").trim(),
  });
}

export async function updateAdminContentBankHierarchyTask(taskId, data) {
  const id = toPositiveInt(taskId);
  if (!id) throw new Error("content bank task id invalid");
  return apiPut(`/admin/content-bank/tasks/${id}`, {
    title: String(data?.title ?? "").trim(),
    order_index: toPositiveInt(data?.order_index),
    is_active: Number(data?.is_active) === 0 ? 0 : 1,
  });
}

export async function deleteAdminContentBankHierarchyTask(taskId) {
  const id = toPositiveInt(taskId);
  if (!id) throw new Error("content bank task id invalid");
  return apiDelete(`/admin/content-bank/tasks/${id}`);
}

export async function fetchAdminContentBankBranches(bankId) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("content bank id invalid");
  return apiGet(`/admin/content-bank/banks/${id}/branches`);
}

export async function createAdminContentBank(data) {
  return apiPost("/admin/content-bank/banks", normalizeContentBankPayload(data));
}

export async function updateAdminContentBank(bankId, data) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("content bank id invalid");
  return apiPut(`/admin/content-bank/banks/${id}`, normalizeContentBankPayload(data));
}

export async function deleteAdminContentBank(bankId) {
  const id = toPositiveInt(bankId);
  if (!id) throw new Error("content bank id invalid");
  return apiDelete(`/admin/content-bank/banks/${id}`);
}

export async function fetchAdminContentBankTasksOverview(dayId) {
  const id = toPositiveInt(dayId);
  if (!id) throw new Error("day id invalid");
  return apiGet(`/admin/content-bank-items/tasks-overview/by-day?day_id=${id}`);
}

export async function fetchAdminContentBankItems(taskId) {
  const id = toPositiveInt(taskId);
  if (!id) throw new Error("task id invalid");
  return apiGet(`/admin/content-bank-items?task_id=${id}`);
}

export async function fetchAdminContentBankTaskItems(bankTaskId) {
  const id = toPositiveInt(bankTaskId);
  if (!id) throw new Error("content bank task id invalid");
  return apiGet(`/admin/content-bank-items/bank-task/items?bank_task_id=${id}`);
}

export async function createAdminContentBankItem(data) {
  return apiPost(
    "/admin/content-bank-items",
    normalizeAdminContentBankItemPayload(data)
  );
}

export async function createAdminContentBankTaskItem(data) {
  const payload = normalizeAdminContentBankItemPayload(data);
  payload.bank_task_id = toPositiveInt(data?.bank_task_id);
  return apiPost("/admin/content-bank-items/bank-task/items", payload);
}

export async function updateAdminContentBankItem(itemId, data) {
  const id = toPositiveInt(itemId);
  if (!id) throw new Error("content bank item id invalid");

  return apiPut(
    `/admin/content-bank-items/${id}`,
    normalizeAdminContentBankItemPayload(data)
  );
}

export async function updateAdminContentBankTaskItem(itemId, data) {
  const id = toPositiveInt(itemId);
  if (!id) throw new Error("content bank item id invalid");
  const payload = normalizeAdminContentBankItemPayload(data);
  payload.bank_task_id = toPositiveInt(data?.bank_task_id);
  return apiPut(`/admin/content-bank-items/bank-task/items/${id}`, payload);
}

export async function deleteAdminContentBankItem(itemId, taskId = null) {
  const id = toPositiveInt(itemId);
  if (!id) throw new Error("content bank item id invalid");
  const taskQuery = toPositiveInt(taskId);
  const suffix = taskQuery ? `?task_id=${taskQuery}` : "";
  return apiDelete(`/admin/content-bank-items/${id}${suffix}`);
}

export async function deleteAdminContentBankTaskItem(itemId, bankTaskId) {
  const id = toPositiveInt(itemId);
  const taskId = toPositiveInt(bankTaskId);
  if (!id) throw new Error("content bank item id invalid");
  if (!taskId) throw new Error("content bank task id invalid");
  return apiDelete(`/admin/content-bank-items/bank-task/items/${id}?bank_task_id=${taskId}`);
}
